import { useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X } from 'lucide-react'
import { create } from 'zustand'
import { cn } from '@/lib/utils'

/** 토스트 메시지 타입 */
type ToastType = 'success' | 'error' | 'warning' | 'info'

/** 토스트 메시지 */
interface ToastMessage {
  id: string
  type: ToastType
  message: string
  duration?: number
}

/** 토스트 스토어 상태 */
interface ToastStore {
  toasts: ToastMessage[]
  addToast: (toast: Omit<ToastMessage, 'id'>) => void
  removeToast: (id: string) => void
}

/** 토스트 상태 관리 (Zustand) */
export const useToastStore = create<ToastStore>((set) => ({
  toasts: [],
  addToast: (toast) => {
    const id = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
    set((state) => ({
      toasts: [...state.toasts, { ...toast, id }],
    }))
  },
  removeToast: (id) => {
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    }))
  },
}))

/** 토스트 편의 함수 */
export const toast = {
  success: (message: string, duration?: number) =>
    useToastStore.getState().addToast({ type: 'success', message, duration }),
  error: (message: string, duration?: number) =>
    useToastStore.getState().addToast({ type: 'error', message, duration }),
  warning: (message: string, duration?: number) =>
    useToastStore.getState().addToast({ type: 'warning', message, duration }),
  info: (message: string, duration?: number) =>
    useToastStore.getState().addToast({ type: 'info', message, duration }),
}

/** 타입별 스타일 */
const typeStyles: Record<ToastType, string> = {
  success: 'bg-cb-secondary-light text-cb-secondary border-cb-secondary',
  error: 'bg-cb-danger-light text-cb-danger border-cb-danger',
  warning: 'bg-amber-50 text-amber-700 border-amber-500',
  info: 'bg-cb-primary-light text-cb-primary border-cb-primary',
}

/** 개별 토스트 아이템 */
function ToastItem({ id, type, message, duration = 4000 }: ToastMessage) {
  const removeToast = useToastStore((state) => state.removeToast)

  const handleClose = useCallback(() => {
    removeToast(id)
  }, [id, removeToast])

  useEffect(() => {
    const timer = setTimeout(handleClose, duration)
    return () => clearTimeout(timer)
  }, [duration, handleClose])

  return (
    <motion.div
      initial={{ opacity: 0, y: -20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -20, scale: 0.95 }}
      transition={{ duration: 0.2 }}
      role="alert"
      className={cn(
        'flex items-center gap-3 rounded-[var(--radius-card)] border-l-4 px-4 py-3 shadow-md',
        'text-body-sm font-medium',
        typeStyles[type]
      )}
    >
      <p className="flex-1">{message}</p>
      <button
        onClick={handleClose}
        className="flex-shrink-0 p-1 rounded-full hover:bg-black/5 transition-colors"
        aria-label="알림 닫기"
      >
        <X size={16} />
      </button>
    </motion.div>
  )
}

/**
 * 토스트 컨테이너 컴포넌트.
 * App.tsx에 한 번 배치하면 전역 토스트 알림을 표시합니다.
 *
 * 사용법:
 * ```tsx
 * import { toast } from '@/components/ui/Toast'
 * toast.success('저장되었습니다.')
 * toast.error('오류가 발생했습니다.')
 * ```
 */
export default function ToastContainer() {
  const toasts = useToastStore((state) => state.toasts)

  return (
    <div
      aria-live="polite"
      aria-label="알림"
      className="fixed top-4 right-4 left-4 z-50 mx-auto flex max-w-md flex-col gap-2 pointer-events-none"
      suppressHydrationWarning
    >
      <AnimatePresence mode="popLayout">
        {toasts.map((t) => (
          <div key={t.id} className="pointer-events-auto">
            <ToastItem {...t} />
          </div>
        ))}
      </AnimatePresence>
    </div>
  )
}
